import type { ZodType } from "zod";
import z from "zod";

export const UPN_SYSTEM =
  "https://www.gov.uk/government/publications/unique-pupil-numbers";

type UpnProblem = "length" | "characters" | "check_letter";

const UPN_LENGTH = 13;

const CHECK_LETTERS = "ABCDEFGHJKLMNPQRTUVWXYZ";

const WELL_FORMED = new RegExp(
  `^[${CHECK_LETTERS}][0-9]{11}[0-9${CHECK_LETTERS}]$`,
);

const PROBLEM_MESSAGE: Record<UpnProblem, string> = {
  length: "personPage.identifier.upnLength",
  characters: "personPage.identifier.upnCharacters",
  check_letter: "personPage.identifier.upnCheckLetter",
};

const characterValue = (character: string): number =>
  /[0-9]/.test(character)
    ? Number(character)
    : CHECK_LETTERS.indexOf(character);

const expectedCheckLetter = (value: string): string =>
  CHECK_LETTERS.charAt(
    Array.from(value.slice(1)).reduce(
      (sum, character, index) => sum + characterValue(character) * (index + 2),
      0,
    ) % CHECK_LETTERS.length,
  );

const upnProblem = (value: string): UpnProblem | null => {
  if (value.length !== UPN_LENGTH) {
    return "length";
  }

  if (!WELL_FORMED.test(value)) {
    return "characters";
  }

  if (value.charAt(0) !== expectedCheckLetter(value)) {
    return "check_letter";
  }

  return null;
};

export type UpnUnbranded = string;

export const UpnSchema = z
  .preprocess(
    (value) =>
      typeof value === "string"
        ? value.replaceAll(/\s/g, "").toUpperCase()
        : value,
    z.string().superRefine((value, context) => {
      const problem = upnProblem(value);

      if (problem !== null) {
        context.addIssue({ code: "custom", message: PROBLEM_MESSAGE[problem] });
      }
    }),
  )
  .brand<"haven-common:Upn">() satisfies ZodType<UpnUnbranded>;

/** A Unique Pupil Number, in capitals and without spacing. */
export type Upn = z.infer<typeof UpnSchema>;
